import { Track } from "@/src/lib/tracking";
import Translations from "@translate/components/Sender/useVoiceRecorder";
import {
  detectPlatform,
  ensureNativeVoiceRecordingPermissionWithMeta,
  getBridge,
} from "@/src/lib/bridge";
import {
  getCompatibleGetUserMedia,
  supportsWebVoiceRecording,
} from "./voiceSupport";

import { useCallback, useEffect, useRef, useState } from "react";
import { I18nUtil } from "@utils/i18n";
import type { MicVAD } from "@ricky0123/vad-web";
import { OspToast } from "@ospweb/sdk-ui";
import type { VoiceRecognitionResult } from "./useCurrentMessageVoiceState";
import { chatClientApiBaseUrl } from "@config/config";
import { useMemoizedFn } from "ahooks";
import { pubsub } from "utils";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";

const I18n = I18nUtil.createScoped(Translations);

type VoiceInputPhase = "idle" | "recording" | "transcribing" | "error";
type VoiceSendIntent = "to_input" | "direct_send";

const uploadApiBaseUrl = (() => {
  const env = process.env.NEXT_PUBLIC_ENV as keyof typeof chatClientApiBaseUrl;
  return chatClientApiBaseUrl[env] || chatClientApiBaseUrl.prod;
})();

const DEFAULT_VAD_CONFIG = {
  positiveSpeechThreshold: 0.35,
  negativeSpeechThreshold: 0.22,
  minSpeechMs: 300,
  redemptionMs: 1800,
  preSpeechPadMs: 1000,
  finalizeWaitMs: 80,
  wavSampleRate: 16000,
  deepSearchVoiceAttachment: false,
  audioConstraints: {
    channelCount: 1,
    echoCancellation: true,
    autoGainControl: true,
    noiseSuppression: true,
  },
};

type VadConfig = typeof DEFAULT_VAD_CONFIG;

let cachedVadConfig: VadConfig | null = null;
let vadConfigFetchPromise: Promise<VadConfig> | null = null;

const fetchVadConfig = async (): Promise<VadConfig> => {
  try {
    const res = await fetch(`${uploadApiBaseUrl}/speech/vad-config`, {
      method: "GET",
      signal: AbortSignal.timeout(5000),
    });
    const json = await res.json();
    if (!res.ok || !json?.success || !json?.data) {
      return DEFAULT_VAD_CONFIG;
    }
    return {
      ...DEFAULT_VAD_CONFIG,
      ...json.data,
      audioConstraints: {
        ...DEFAULT_VAD_CONFIG.audioConstraints,
        ...json.data?.audioConstraints,
      },
    };
  } catch {
    return DEFAULT_VAD_CONFIG;
  }
};

const getVadConfig = async (): Promise<VadConfig> => {
  if (cachedVadConfig) {
    return cachedVadConfig;
  }
  if (!vadConfigFetchPromise) {
    vadConfigFetchPromise = fetchVadConfig().then((cfg) => {
      cachedVadConfig = cfg;
      vadConfigFetchPromise = null;
      return cfg;
    });
  }
  return vadConfigFetchPromise;
};

/** Root-relative path; use {@link getVadAssetBaseUrl} for ORT dynamic import when scripts load cross-origin (CDN). */
const VAD_ASSET_BASE_PATH = "/vad/";

/**
 * ONNX Runtime loads WASM via dynamic import(). Root-relative URLs break when the caller chunk is served from a
 * different origin than the document (e.g. Next.js assetPrefix CDN), yielding "base URL is about:blank".
 */
const getVadAssetBaseUrl = (): string => {
  if (typeof window === "undefined") {
    return VAD_ASSET_BASE_PATH;
  }
  return new URL(VAD_ASSET_BASE_PATH, window.location.href).href;
};

const VAD_MIC_RETRY_MS = 180;
const VAD_START_RETRY_MS = 220;

let activeAudioConstraints: MediaTrackConstraints =
  DEFAULT_VAD_CONFIG.audioConstraints;

function shouldUseEmbeddedMicFallbacks(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return detectPlatform() !== "web" || getBridge().isInApp();
  } catch {
    return false;
  }
}

/**
 * Single getUserMedia path for MicVAD. Avoid echoCancellation-heavy defaults first on WebView; optional fallbacks.
 */
async function getUserMediaWithWebViewFallbacks(): Promise<MediaStream> {
  const embedded = shouldUseEmbeddedMicFallbacks();
  const configuredAudioConstraints =
    Object.keys(activeAudioConstraints).length > 0
      ? activeAudioConstraints
      : DEFAULT_VAD_CONFIG.audioConstraints;
  const getUserMedia = getCompatibleGetUserMedia();
  if (!getUserMedia) {
    throw new Error("GET_USER_MEDIA_UNSUPPORTED");
  }
  const attempts: { label: string; audio: MediaTrackConstraints | boolean }[] =
    [
      { label: "audio_true", audio: true },
      {
        label: "vad_configured_constraints",
        audio: configuredAudioConstraints,
      },
      { label: "channelCount_1", audio: { channelCount: 1 } },
      {
        label: "processing_off",
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      },
      {
        label: "ch1_processing_off",
        audio: {
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      },
    ];

  const maxIdx = embedded ? attempts.length : 2;
  let lastErr: unknown;

  for (let i = 0; i < maxIdx; i += 1) {
    if (i > 0) {
      await new Promise<void>((r) => {
        setTimeout(r, VAD_MIC_RETRY_MS);
      });
    }
    try {
      return await getUserMedia({
        audio: attempts[i].audio,
      });
    } catch (e) {
      lastErr = e;
      Track.log("warn", "voice", "getUserMedia attempt failed", {
        attempt: attempts[i].label,
        name: e instanceof Error ? e.name : undefined,
        message: e instanceof Error ? e.message : undefined,
      });
      if (e instanceof DOMException && e.name === "NotAllowedError") {
        throw e;
      }
      const name = e instanceof Error ? e.name : "";
      const retry =
        embedded &&
        (name === "NotReadableError" ||
          name === "OverconstrainedError" ||
          name === "AbortError");
      if (!retry) {
        throw e;
      }
    }
  }

  if (embedded && navigator.mediaDevices?.enumerateDevices) {
    await new Promise<void>((r) => {
      setTimeout(r, VAD_MIC_RETRY_MS);
    });
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(
        (d) => d.kind === "audioinput" && d.deviceId,
      );
      for (const d of inputs) {
        for (const exact of [true, false]) {
          try {
            return await getUserMedia({
              audio: exact
                ? { deviceId: { exact: d.deviceId } }
                : { deviceId: { ideal: d.deviceId } },
            });
          } catch (e) {
            lastErr = e;
            if (e instanceof DOMException && e.name === "NotAllowedError") {
              throw e;
            }
          }
        }
      }
    } catch {
      /* enumerate optional */
    }
  }

  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}
type VadRuntimeBindings = {
  onSpeechEnd: ((audio: Float32Array) => void) | null;
};

type VadSessionOwner = symbol;

type ActiveVadSession = VadRuntimeBindings & {
  owner: VadSessionOwner;
};

let vadSingletonPromise: Promise<MicVAD> | null = null;
let activeVadSession: ActiveVadSession | null = null;
let vadNeedsRecreate = false;
/**
 * iOS 修复：在用户手势作用域内预先创建并 resume 的 AudioContext。
 * 必须在任何 await 之前（用户手势同步上下文中）赋值，VAD 下次初始化时会消费它。
 * 避免 vad.start() 在手势作用域外调用 new AudioContext() 导致 suspended 状态。
 */
let pendingUserGestureAudioCtx: AudioContext | null = null;

const getVadUserMedia = async (): Promise<MediaStream> =>
  getUserMediaWithWebViewFallbacks();

const destroyVadSingleton = async () => {
  const currentPromise = vadSingletonPromise;
  vadSingletonPromise = null;
  activeVadSession = null;
  vadNeedsRecreate = false;

  if (!currentPromise) {
    return;
  }

  try {
    const vad = await currentPromise;
    await vad.destroy();
  } catch (error) {
    Track.log("warn", "voice", "destroy VAD singleton failed", {
      cause: String(error),
    });
  }
};

const getVadSingleton = async (): Promise<MicVAD> => {
  if (vadNeedsRecreate) {
    await destroyVadSingleton();
  }
  if (!vadSingletonPromise) {
    vadSingletonPromise = getVadConfig()
      .then(async (cfg) => {
        activeAudioConstraints = cfg.audioConstraints;
        const audioCtxForVad = pendingUserGestureAudioCtx;
        pendingUserGestureAudioCtx = null;
        const { MicVAD: MicVADClass } = await import("@ricky0123/vad-web");
        return MicVADClass.new({
          model: "v5",
          startOnLoad: false,
          baseAssetPath: getVadAssetBaseUrl(),
          onnxWASMBasePath: getVadAssetBaseUrl(),
          positiveSpeechThreshold: cfg.positiveSpeechThreshold,
          negativeSpeechThreshold: cfg.negativeSpeechThreshold,
          minSpeechMs: cfg.minSpeechMs,
          redemptionMs: cfg.redemptionMs,
          preSpeechPadMs: cfg.preSpeechPadMs,
          submitUserSpeechOnPause: true,
          processorType: "ScriptProcessor",
          ...(audioCtxForVad ? { audioContext: audioCtxForVad } : {}),
          getStream: getVadUserMedia,
          resumeStream: getVadUserMedia,
          pauseStream: async (stream) => {
            stream.getTracks().forEach((track) => track.stop());
          },
          onSpeechEnd: (audio: Float32Array) => {
            activeVadSession?.onSpeechEnd?.(audio);
          },
        });
      })
      .catch((error) => {
        vadSingletonPromise = null;
        throw error;
      });
  }

  return vadSingletonPromise;
};

const markVadForRecreate = () => {
  vadNeedsRecreate = true;
  activeVadSession = null;
};

const startVadSession = async (
  owner: VadSessionOwner,
  bindings: VadRuntimeBindings,
): Promise<MicVAD> => {
  if (activeVadSession && activeVadSession.owner !== owner) {
    throw new Error("VAD_SESSION_BUSY");
  }

  try {
    const vad = await getVadSingleton();
    // IMPORTANT: set activeVadSession AFTER getVadSingleton() resolves.
    // If markVadForRecreate() was called before this (e.g. iOS AudioContext fix),
    // getVadSingleton() internally calls destroyVadSingleton() which sets
    // activeVadSession = null — wiping any binding set before this await.
    // Speech events only fire after vad.start(), so binding here is safe.
    activeVadSession = {
      owner,
      ...bindings,
    };
    await vad.start();
    return vad;
  } catch (error) {
    if (activeVadSession?.owner === owner) {
      activeVadSession = null;
    }
    throw error;
  }
};

const pauseVadSession = async (owner: VadSessionOwner): Promise<void> => {
  if (activeVadSession?.owner !== owner) {
    return;
  }
  const vad = await getVadSingleton();
  await vad.pause();
};

const clearVadSession = (owner: VadSessionOwner) => {
  if (activeVadSession?.owner === owner) {
    activeVadSession = null;
  }
};

function floatTo16BitPCM(float32Samples: Float32Array): Int16Array {
  const pcm = new Int16Array(float32Samples.length);
  for (let i = 0; i < float32Samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, float32Samples[i]));
    pcm[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return pcm;
}

function buildWavBlob(float32Samples: Float32Array, sampleRate: number): Blob {
  const pcm = floatTo16BitPCM(float32Samples);
  const byteRate = sampleRate * 2;
  const blockAlign = 2;
  const buffer = new ArrayBuffer(44 + pcm.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + pcm.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, pcm.length * 2, true);

  for (let i = 0; i < pcm.length; i += 1) {
    view.setInt16(44 + i * 2, pcm[i], true);
  }

  return new Blob([buffer], { type: "audio/wav" });
}

export interface UseVoiceRecorderOptions {
  disabled?: boolean;
  loading?: boolean;
  authHeaders: Record<string, string>;
  fingerprint?: string | number | null;
  onTranscribeComplete: (result: VoiceRecognitionResult) => void;
  onDirectSendComplete: (result: VoiceRecognitionResult) => void;
  onError?: (error: Error) => void;
}

export interface UseVoiceRecorderReturn {
  voicePhase: VoiceInputPhase;
  sendIntent: VoiceSendIntent | null;
  analyserNode: AnalyserNode | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  sendRecording: () => void;
  cancelTranscribe: () => void;
}

export function useVoiceRecorder({
  disabled,
  loading,
  authHeaders,
  fingerprint,
  onTranscribeComplete,
  onDirectSendComplete,
  onError,
}: UseVoiceRecorderOptions): UseVoiceRecorderReturn {
  const [voicePhase, setVoicePhase] = useState<VoiceInputPhase>("idle");
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [sendIntent, setSendIntent] = useState<VoiceSendIntent | null>(null);

  const sessionOwnerRef = useRef<VadSessionOwner>(Symbol("voice-recorder"));
  const vadRef = useRef<MicVAD | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const micVadAnalyserTapRef = useRef<{
    source: MediaStreamAudioSourceNode;
    analyser: AnalyserNode;
  } | null>(null);
  const speechSegmentsRef = useRef<Float32Array[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingIntentRef = useRef<VoiceSendIntent | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);

  const cleanupResources = useCallback(() => {
    if (micVadAnalyserTapRef.current) {
      try {
        const { source, analyser } = micVadAnalyserTapRef.current;
        source.disconnect(analyser);
      } catch {
        /* ignore */
      }
      micVadAnalyserTapRef.current = null;
    }
    void pauseVadSession(sessionOwnerRef.current);
    vadRef.current = null;
    clearVadSession(sessionOwnerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    speechSegmentsRef.current = [];
    recordingStartedAtRef.current = null;
    setAnalyserNode(null);
  }, []);

  /**
   * Fan out from MicVAD's MediaStreamSource so we never create a second MediaStreamAudioSourceNode for the
   * same mic (Android WebView often throws NotReadableError on the second source).
   */
  const attachAnalyserToMicVad = useCallback((vad: MicVAD) => {
    try {
      const getter = (
        vad as unknown as {
          getAudioInstances: () => {
            stream: MediaStream;
            audioContext: AudioContext;
            mediaStreamAudioSourceNode: MediaStreamAudioSourceNode;
          };
        }
      ).getAudioInstances;
      const { stream, audioContext, mediaStreamAudioSourceNode } =
        getter.call(vad);
      streamRef.current = stream;
      if (micVadAnalyserTapRef.current) {
        try {
          const { source, analyser } = micVadAnalyserTapRef.current;
          source.disconnect(analyser);
        } catch {
          /* ignore */
        }
        micVadAnalyserTapRef.current = null;
      }
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      mediaStreamAudioSourceNode.connect(analyser);
      micVadAnalyserTapRef.current = {
        source: mediaStreamAudioSourceNode,
        analyser,
      };
      setAnalyserNode(analyser);
      if (
        audioContext.state === "suspended" ||
        (audioContext.state as string) === "interrupted"
      ) {
        void audioContext.resume().catch((error) => {
          Track.log("warn", "voice", "MicVAD AudioContext resume failed", {
            cause: String(error),
          });
        });
      }
    } catch (error) {
      Track.log("warn", "voice", "attachAnalyserToMicVad failed", {
        cause: String(error),
      });
    }
  }, []);

  const mergeSpeechSegments = useCallback((): Float32Array | null => {
    const segments = speechSegmentsRef.current;
    const cfg = cachedVadConfig ?? DEFAULT_VAD_CONFIG;
    if (!segments.length) {
      return null;
    }
    const trimLeadingSamples = Math.floor(
      (cfg.wavSampleRate * cfg.preSpeechPadMs) / 1000,
    );
    const normalizedSegments = segments.map((seg, index) => {
      if (index === 0 || trimLeadingSamples <= 0) {
        return seg;
      }
      if (seg.length <= trimLeadingSamples) {
        return new Float32Array(0);
      }
      return seg.subarray(trimLeadingSamples);
    });
    const totalLength = normalizedSegments.reduce(
      (sum, seg) => sum + seg.length,
      0,
    );
    if (totalLength <= 0) {
      return null;
    }
    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const seg of normalizedSegments) {
      merged.set(seg, offset);
      offset += seg.length;
    }
    Track.log("info", "voice", "merged speech segments", {
      segmentCount: segments.length,
      totalLength,
    });
    return merged;
  }, []);

  const doTranscribe = useMemoizedFn(
    async (
      audioBlob: Blob,
      abortController: AbortController,
    ): Promise<{
      text: string;
      emotionId: string;
      durationMs: number | null;
    }> => {
      const timestamp = Date.now();
      const form = new FormData();
      form.append("audio", audioBlob, `voice-${timestamp}.wav`);

      const res = await fetch(`${uploadApiBaseUrl}/speech/transcribe`, {
        method: "POST",
        headers: {
          ...authHeaders,
          ...(fingerprint ? { guestId: String(fingerprint) } : {}),
        },
        body: form,
        signal: abortController.signal,
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.msg || "Speech recognition failed");
      }
      const text = json?.data?.text?.trim();
      if (!text) {
        throw new Error("Speech recognition failed");
      }
      const emotionId =
        typeof json?.data?.emotionId === "string" && json.data.emotionId.trim()
          ? json.data.emotionId.trim()
          : "";
      const durationMs =
        typeof json?.data?.durationMs === "number" &&
        Number.isFinite(json.data.durationMs)
          ? Math.max(0, json.data.durationMs)
          : null;
      return {
        text,
        emotionId,
        durationMs,
      };
    },
  );

  const handleTranscribeOnly = useMemoizedFn(
    async (audioBlob: Blob, startedAt: number) => {
      setVoicePhase("transcribing");
      setSendIntent("to_input");
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const result = await doTranscribe(audioBlob, abortController);
        if (abortController.signal.aborted) {
          return;
        }
        setSendIntent(null);
        setVoicePhase("idle");
        onTranscribeComplete({
          startedAt,
          durationMs: result.durationMs ?? Math.max(Date.now() - startedAt, 0),
          text: result.text,
          emotionId: result.emotionId,
          audioBlob,
        });
      } catch (err: any) {
        if (err?.name === "AbortError") {
          setSendIntent(null);
          setVoicePhase("idle");
          return;
        }
        OspToast.error({ content: I18n.t("Speech recognition failed") });
        setSendIntent(null);
        setVoicePhase("idle");
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        abortControllerRef.current = null;
      }
    },
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      cleanupResources();
    };
  }, [cleanupResources]);

  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "hidden") {
        return;
      }
      markVadForRecreate();
      if (voicePhase === "recording") {
        cleanupResources();
        setSendIntent(null);
        setVoicePhase("idle");
      }
    };

    const handlePageHide = () => {
      markVadForRecreate();
      if (voicePhase === "recording") {
        cleanupResources();
        setSendIntent(null);
        setVoicePhase("idle");
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [cleanupResources, voicePhase]);

  const handleTranscribeAndSend = useMemoizedFn(
    async (audioBlob: Blob, startedAt: number) => {
      setVoicePhase("transcribing");
      setSendIntent("direct_send");
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const result = await doTranscribe(audioBlob, abortController);

        if (abortController.signal.aborted) {
          return;
        }

        setSendIntent(null);
        setVoicePhase("idle");
        onDirectSendComplete({
          startedAt,
          durationMs: result.durationMs ?? Math.max(Date.now() - startedAt, 0),
          text: result.text,
          emotionId: result.emotionId,
          audioBlob,
        });
      } catch (err: any) {
        if (err?.name === "AbortError") {
          setSendIntent(null);
          setVoicePhase("idle");
          return;
        }
        OspToast.error({
          content: I18n.t("Speech recognition failed"),
        });
        setSendIntent(null);
        setVoicePhase("idle");
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        abortControllerRef.current = null;
      }
    },
  );

  const startRecording = useMemoizedFn(async () => {
    if (disabled || loading || voicePhase !== "idle") {
      return;
    }
    // Native app: voice is handled via native bridge + getUserMedia in WebView,
    // skip the web API capability check here — let ensureNativeVoiceRecordingPermission
    // and the VAD bootstrap surface the real error if the device truly can't record.
    const isEmbedded = shouldUseEmbeddedMicFallbacks();
    if (!isEmbedded && !supportsWebVoiceRecording()) {
      OspToast.error({
        content: I18n.t("Recording failed"),
      });
      return;
    }

    // ─── iOS AudioContext fix ────────────────────────────────────────────────
    // On iOS (especially 14.3–14.4), new AudioContext() created outside a user
    // gesture starts in "suspended" state and resume() is restricted.
    // vad.start() creates new AudioContext() internally (vad-web 0.0.30 L222)
    // without calling resume(), so ScriptProcessor.onaudioprocess never fires.
    //
    // Fix: synchronously create+resume an AudioContext HERE (within the user
    // gesture sync scope, BEFORE any await), then pass it to getVadSingleton
    // via pendingUserGestureAudioCtx so the VAD reuses an already-running ctx.
    //
    // IMPORTANT: guard with iOS-only check. markVadForRecreate() clears
    // activeVadSession at module level. If called on web/Android, destroyVadSingleton()
    // (triggered inside getVadSingleton) will null out activeVadSession AFTER
    // startVadSession has already set its onSpeechEnd binding — causing every
    // speech segment to be silently dropped (length=0 / "No valid content").
    // ────────────────────────────────────────────────────────────────────────
    if (typeof window !== "undefined" && detectPlatform() === "ios") {
      const AudioCtorClass =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (AudioCtorClass) {
        try {
          const ctx = new AudioCtorClass();
          // Fire-and-forget resume within user gesture scope
          void ctx.resume();
          // Store for getVadSingleton to pick up; force VAD recreate so it
          // uses this running context instead of creating a new suspended one.
          pendingUserGestureAudioCtx = ctx;
          markVadForRecreate();
          Track.log(
            "info",
            "voice",
            "pre-created AudioContext for iOS unlock",
            {
              state: ctx.state,
            },
          );
        } catch (e) {
          Track.log("warn", "voice", "iOS AudioContext pre-create failed", {
            cause: String(e),
          });
        }
      }
    }

    const shellVoicePerm =
      await ensureNativeVoiceRecordingPermissionWithMeta(getBridge());
    if (shellVoicePerm.verdict === "denied") {
      if (!shellVoicePerm.dialogShown) {
        pubsub.emit(pubSubKeyMap.OPEN_VOICE_PERMISSION_MODAL);
      }
      return;
    }
    try {
      speechSegmentsRef.current = [];
      pendingIntentRef.current = null;
      recordingStartedAtRef.current = Date.now();
      const startOneSession = async () => {
        return startVadSession(sessionOwnerRef.current, {
          onSpeechEnd: (audio: Float32Array) => {
            speechSegmentsRef.current.push(audio);
          },
        });
      };

      let vad: MicVAD;
      try {
        vad = await startOneSession();
      } catch (firstErr: any) {
        const recoverable =
          firstErr?.name === "NotReadableError" ||
          firstErr?.name === "AbortError" ||
          firstErr?.name === "OverconstrainedError" ||
          firstErr?.name === "InvalidStateError";
        if (!recoverable) {
          throw firstErr;
        }
        Track.log(
          "warn",
          "voice",
          "VAD start failed once, retry with recreate",
          {
            name: firstErr?.name,
            message: firstErr?.message,
          },
        );
        markVadForRecreate();
        await new Promise<void>((resolve) => {
          setTimeout(resolve, VAD_START_RETRY_MS);
        });
        vad = await startOneSession();
      }

      attachAnalyserToMicVad(vad);
      vadRef.current = vad;
      setVoicePhase("recording");
    } catch (err: any) {
      if (err?.message === "VAD_SESSION_BUSY") {
        OspToast.warning({
          content: I18n.t("Voice input is already in use"),
        });
        return;
      }
      if (
        err?.name === "NotAllowedError" ||
        err?.name === "PermissionDeniedError" ||
        err?.name === "NotReadableError"
      ) {
        cleanupResources();
        OspToast.error({
          content: I18n.t(
            err?.name === "NotReadableError"
              ? "Recording failed"
              : "Microphone permission denied",
          ),
        });
        return;
      }
      cleanupResources();
      OspToast.error({ content: I18n.t("Recording failed") });
    }
  });

  const stopRecording = useMemoizedFn(() => {
    if (voicePhase !== "recording" || !vadRef.current) {
      return;
    }
    const cfg = cachedVadConfig ?? DEFAULT_VAD_CONFIG;
    pendingIntentRef.current = "to_input";
    void pauseVadSession(sessionOwnerRef.current).finally(() => {
      setTimeout(() => {
        const intent = pendingIntentRef.current;
        pendingIntentRef.current = null;
        const speech = mergeSpeechSegments();
        const startedAt = recordingStartedAtRef.current ?? Date.now();
        cleanupResources();
        if (!speech || speech.length === 0) {
          setSendIntent(null);
          setVoicePhase("idle");
          OspToast.warning({ content: I18n.t("No valid content detected") });
          return;
        }
        const audioBlob = buildWavBlob(speech, cfg.wavSampleRate);
        if (intent === "to_input") {
          void handleTranscribeOnly(audioBlob, startedAt);
        }
      }, cfg.finalizeWaitMs);
    });
  });

  const sendRecording = useMemoizedFn(() => {
    if (voicePhase !== "recording" || !vadRef.current) {
      return;
    }
    const cfg = cachedVadConfig ?? DEFAULT_VAD_CONFIG;
    pendingIntentRef.current = "direct_send";
    void pauseVadSession(sessionOwnerRef.current).finally(() => {
      setTimeout(() => {
        const intent = pendingIntentRef.current;
        pendingIntentRef.current = null;
        const speech = mergeSpeechSegments();
        const startedAt = recordingStartedAtRef.current ?? Date.now();
        cleanupResources();
        if (!speech || speech.length === 0) {
          setSendIntent(null);
          setVoicePhase("idle");
          OspToast.warning({ content: I18n.t("No valid content detected") });
          return;
        }
        const audioBlob = buildWavBlob(speech, cfg.wavSampleRate);
        if (intent === "direct_send") {
          void handleTranscribeAndSend(audioBlob, startedAt);
        }
      }, cfg.finalizeWaitMs);
    });
  });

  const cancelTranscribe = useMemoizedFn(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setSendIntent(null);
    setVoicePhase("idle");
  });

  return {
    voicePhase,
    sendIntent,
    analyserNode,
    startRecording,
    stopRecording,
    sendRecording,
    cancelTranscribe,
  };
}

export const isDeepSearchVoiceAttachmentEnabled = (): boolean =>
  (cachedVadConfig ?? DEFAULT_VAD_CONFIG).deepSearchVoiceAttachment;
